import {
  WORK_ITEM_DESCRIPTION_MAX, WORK_ITEM_PRIORITIES, WORK_ITEM_PROJECT_MAX, WORK_ITEM_STATUSES,
  WORK_ITEM_TITLE_MAX, type WorkItemDetailView, type WorkItemPriority, type WorkItemStatus,
} from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useId, useRef, useState } from 'react';
import { errorText } from '../../lib/copy';
import {
  createWorkItem, fetchAssignees, fetchProjects, PRIORITY_LABEL, refreshWork, STATUS_LABEL, workKeys,
} from '../../lib/workItems';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { Select } from '../../ui/Select';
import styles from './WorkItemPanel.module.css';

export function CreateWorkItemDialog({ defaultStatus, onClose, onCreated }: {
  defaultStatus: WorkItemStatus; onClose: () => void; onCreated: (item: WorkItemDetailView) => void;
}) {
  const client = useQueryClient();
  const assignees = useQuery({ queryKey: workKeys.assignees, queryFn: fetchAssignees });
  const projects = useQuery({ queryKey: workKeys.projects, queryFn: fetchProjects });
  const formId = useId();
  const prefix = useId();
  const titleInput = useRef<HTMLInputElement>(null);
  const working = useRef(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [project, setProject] = useState('');
  const [assigneeId, setAssigneeId] = useState('');
  const [priority, setPriority] = useState<WorkItemPriority>('NORMAL');
  const [status, setStatus] = useState<WorkItemStatus>(defaultStatus);
  const [dueDate, setDueDate] = useState('');
  const [publishDate, setPublishDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ConfirmDialog enfoca Cancelar al abrir: este formulario empieza en el título.
  useEffect(() => {
    const frame = requestAnimationFrame(() => titleInput.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || working.current) return;
    working.current = true;
    setBusy(true);
    setError(null);
    try {
      const item = await createWorkItem({
        title: title.trim(), status, priority,
        ...(description.trim() ? { description } : {}),
        ...(project.trim() ? { project: project.trim() } : {}),
        ...(assigneeId ? { assigneeId } : {}),
        ...(dueDate ? { dueDate } : {}),
        ...(publishDate ? { publishDate } : {}),
      });
      await refreshWork(client);
      onCreated(item);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      working.current = false;
      setBusy(false);
    }
  }

  return (
    <ConfirmDialog open variant="form" title="Nueva tarea" formId={formId} confirmLabel="Crear tarea" busyLabel="Creando…"
      confirmDisabled={!title.trim()} busy={busy} onConfirm={() => undefined} onCancel={onClose}>
      <form id={formId} className={`${styles.form} ${styles.dialogForm}`} onSubmit={(event) => void submit(event)}>
        <label className="field" htmlFor={`${prefix}-title`}><span>Título</span>
          <input ref={titleInput} id={`${prefix}-title`} className="input" value={title} maxLength={WORK_ITEM_TITLE_MAX} required autoFocus
            autoComplete="off" disabled={busy} onChange={(event) => setTitle(event.target.value)} placeholder="¿Qué hay que hacer?" />
        </label>
        <label className="field" htmlFor={`${prefix}-description`}><span>Descripción</span>
          <textarea id={`${prefix}-description`} className={`input ${styles.textarea}`} value={description} rows={3}
            maxLength={WORK_ITEM_DESCRIPTION_MAX} disabled={busy} onChange={(event) => setDescription(event.target.value)} />
        </label>
        <label className="field" htmlFor={`${prefix}-project`}><span>Proyecto</span>
          <input id={`${prefix}-project`} className="input" value={project} list={`${prefix}-projects`} maxLength={WORK_ITEM_PROJECT_MAX}
            autoComplete="off" disabled={busy} onChange={(event) => setProject(event.target.value)} placeholder="Sin proyecto" />
        </label>
        <datalist id={`${prefix}-projects`}>{projects.data?.map((value) => <option key={value} value={value} />)}</datalist>
        {projects.isError && <p className={styles.error} role="alert">No se pudieron cargar las sugerencias de proyectos. {errorText(projects.error)}</p>}
        <Select label="Responsable" value={assigneeId} onChange={setAssigneeId} disabled={busy || assignees.isPending || assignees.isError}
          options={[{ value: '', label: 'Sin responsable' }, ...(assignees.data ?? []).map((person) => ({ value: person.id, label: person.displayName }))]} />
        {assignees.isPending && <p className={styles.hint} role="status">Cargando responsables…</p>}
        {assignees.isError && <div className={styles.feedback}>
          <p className={styles.error} role="alert">No se pudieron cargar los responsables. {errorText(assignees.error)}</p>
          <button type="button" className="btn btn-secondary" onClick={() => void assignees.refetch()}>Reintentar</button>
        </div>}
        <div className={styles.grid}>
          <Select label="Prioridad" value={priority} onChange={setPriority} disabled={busy}
            options={WORK_ITEM_PRIORITIES.map((value) => ({ value, label: PRIORITY_LABEL[value] }))} />
          <Select label="Estado" value={status} onChange={setStatus} disabled={busy}
            options={WORK_ITEM_STATUSES.map((value) => ({ value, label: STATUS_LABEL[value] }))} />
        </div>
        <div className={styles.grid}>
          <label className="field" htmlFor={`${prefix}-due`}><span>Fecha de entrega</span>
            <input id={`${prefix}-due`} className="input" type="date" value={dueDate} disabled={busy} onChange={(event) => setDueDate(event.target.value)} />
          </label>
          <label className="field" htmlFor={`${prefix}-publish`}><span>Fecha de publicación</span>
            <input id={`${prefix}-publish`} className="input" type="date" value={publishDate} disabled={busy} onChange={(event) => setPublishDate(event.target.value)} />
          </label>
        </div>
      </form>
      {error && <p className={styles.error} role="alert">{error}</p>}
    </ConfirmDialog>
  );
}
